
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Download, Eye } from "lucide-react";
import { supabase } from "@/shared/integrations/supabase/client";

type AssetPhoto = {
  id: string;
  document_name: string;
  file_path: string | null;
};

interface AssetPhotoGridProps {
  assetId: string;
  title?: string;
  className?: string;
}

export default function AssetPhotoGrid({ assetId, title = "Images", className = "" }: AssetPhotoGridProps) {
  const [photos, setPhotos] = useState<AssetPhoto[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  const cacheKey = useMemo(
    () => photos.map(p => `${p.id}:${p.file_path || ""}`).join("|"),
    [photos]
  );

  useEffect(() => {
    let cancelled = false;
    async function fetchPhotos() {
      setLoading(true);
      const { data, error } = await supabase
        .from("asset_documents")
        .select("id, document_name, file_path")
        .is("deleted_at", null)
        .eq("asset_id", assetId)
        .eq("document_type", "photo")
        .order("created_at", { ascending: false });
      if (!cancelled) {
        if (!error && data) {
          setPhotos(data as AssetPhoto[]);
        } else {
          console.error("Failed to load asset photos", error);
          setPhotos([]);
        }
        setLoading(false);
      }
    }
    fetchPhotos();
    return () => {
      cancelled = true;
    };
  }, [assetId]);

  useEffect(() => {
    let cancelled = false;
    async function signUrls() {
      if (photos.length === 0) {
        setUrls({});
        return;
      }
      const entries: Array<[string, string]> = [];
      await Promise.all(
        photos
          .filter(p => !!p.file_path)
          .map(async (p) => {
            const { data, error } = await supabase.storage
              .from("asset-documents")
              .createSignedUrl(p.file_path as string, 3600);
            if (!error && data?.signedUrl) {
              entries.push([p.id, data.signedUrl]);
            }
          })
      );
      if (!cancelled) {
        setUrls(Object.fromEntries(entries));
      }
    }
    signUrls();
    return () => {
      cancelled = true;
    };
  }, [cacheKey]);

  const downloadPhoto = async (photo: AssetPhoto) => {
    if (!photo.file_path) return;
    const { data, error } = await supabase.storage.from("asset-documents").download(photo.file_path);
    if (error || !data) return;
    const blobUrl = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = photo.document_name || "photo";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  };

  return (
    <div className={className}>
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground">Preview asset photos</p>
      </div>

      {loading ? (
        <Card className="mt-3">
          <CardContent className="p-8 text-center text-muted-foreground">
            Loading photos...
          </CardContent>
        </Card>
      ) : photos.length === 0 ? (
        <Card className="mt-3">
          <CardContent className="p-8 text-center text-muted-foreground">
            No photos uploaded yet.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
          {photos.map((photo) => (
            <Card key={photo.id} className="overflow-hidden">
              <CardContent className="p-2">
                <div className="aspect-[16/9] bg-muted rounded flex items-center justify-center overflow-hidden">
                  {urls[photo.id] ? (
                    <img
                      src={urls[photo.id]}
                      alt={photo.document_name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <Eye className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm text-muted-foreground truncate" title={photo.document_name}>
                    {photo.document_name}
                  </span>
                  <div className="flex space-x-1">
                    <Button variant="ghost" size="sm" onClick={() => downloadPhoto(photo)} aria-label="Download photo">
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
